import axios from 'axios'
import isNil from 'lodash/isNil'

export default {
  deleteSwiftAidAccount() {
    return axios.delete(`/me`)
  },
  // NOTE: user
  fetchUserStatements() {
    return axios.get(`/me/statements`)
  },
  fetchStatementYears() {
    return axios.get(`/me/statements/years`)
  },
  postDonorInformation(payload: any) {
    return axios.post(`/me/details`, payload)
  },
  fetchUser() {
    return axios.get(`/me`)
  },
  // Provision a Swiftaid account for the current Auth0 identity. Called when
  // GET /me 404s (no donor yet). `ref` is applied only when a new donor is
  // created. Replaces the old GET /me?createUser=true flow.
  // `cancelDeletion` cancels a scheduled deletion instead of getting a 409.
  createUser(referralLink: string | null = null, cancelDeletion = false) {
    const queryParams: string[] = []

    if (!isNil(referralLink)) {
      queryParams.push(`ref=${referralLink}`)
    }

    if (cancelDeletion) {
      queryParams.push('cancelDeletion=true')
    }

    const queryParamsString =
      queryParams.length > 0 ? `?${queryParams.join('&')}` : ''

    return axios.post(`/me${queryParamsString}`)
  },
  markDashboardSeen() {
    return axios.post('/me/dashboard-seen')
  },
  // NOTE: authorisation
  fetchAuth() {
    return axios.get('/me/authorisation', {
      validateStatus: (status) => {
        return (
          (status >= 200 && status < 300) || status === 404 || status === 409
        )
      },
    })
  },
  postAuth() {
    return axios.post('/me/authorisation')
  },
  deleteAuth(payload: { endDate: string }) {
    const endDate = payload.endDate || ''
    return axios.delete(`/me/authorisation?endDate=${endDate}`, {
      validateStatus: (status) => {
        return (
          (status >= 200 && status < 300) || status === 404 || status === 409
        )
      },
    })
  },
  // NOTE: donations
  fetchDonorDonations() {
    return axios.get('/me/donations')
  },
  fetchManualDonations() {
    return axios.get('/me/donations/manual', {
      validateStatus: (status) =>
        (status >= 200 && status < 300) || status === 404,
    })
  },
  fetchFoundForYouDonations() {
    return axios.get('/me/donations/found-for-you', {
      validateStatus: (status) =>
        (status >= 200 && status < 300) || status === 404,
    })
  },
  acceptFoundForYouGiftAid(id: string) {
    return axios.post(
      `/me/donations/found-for-you/${encodeURIComponent(id)}/accept-gift-aid`
    )
  },
  deleteFoundForYouDonation(id: string) {
    return axios.delete(
      `/me/donations/found-for-you/${encodeURIComponent(id)}`,
      {
        validateStatus: (status) =>
          (status >= 200 && status < 300) || status === 204,
      }
    )
  },
  postManualDonation(payload: {
    charity: string
    amount: number
    giftaid?: number
    giftAidDeclared?: boolean
    time: string
    notes?: string
  }) {
    return axios.post('/me/donations/manual', payload)
  },
  putManualDonation(
    id: string,
    payload: {
      charity?: string
      amount?: number
      giftaid?: number
      giftAidDeclared?: boolean
      time?: string
      notes?: string
    }
  ) {
    return axios.put(`/me/donations/manual/${encodeURIComponent(id)}`, payload)
  },
  deleteManualDonation(id: string) {
    return axios.delete(`/me/donations/manual/${encodeURIComponent(id)}`, {
      validateStatus: (status) =>
        (status >= 200 && status < 300) || status === 204,
    })
  },
  fetchRecurringDonations() {
    return axios.get('/me/donations/recurring', {
      validateStatus: (status) =>
        (status >= 200 && status < 300) || status === 404,
    })
  },
  previewRecurringDonation(payload: Record<string, unknown>) {
    return axios.post('/me/donations/recurring/preview', payload, {
      validateStatus: (status) =>
        (status >= 200 && status < 300) || status === 400,
    })
  },
  postRecurringDonation(payload: Record<string, unknown>) {
    return axios.post('/me/donations/recurring', payload, {
      validateStatus: (status) =>
        (status >= 200 && status < 300) || status === 409 || status === 422,
    })
  },
  putRecurringDonation(id: string, payload: Record<string, unknown>) {
    return axios.put(
      `/me/donations/recurring/${encodeURIComponent(id)}`,
      payload
    )
  },
  stopRecurringDonation(id: string) {
    return axios.post(`/me/donations/recurring/${encodeURIComponent(id)}/stop`)
  },
  restartRecurringDonation(id: string, payload: Record<string, unknown>) {
    return axios.post(
      `/me/donations/recurring/${encodeURIComponent(id)}/restart`,
      payload
    )
  },
  skipRecurringNextOccurrence(id: string) {
    return axios.post(
      `/me/donations/recurring/${encodeURIComponent(id)}/skip-next`
    )
  },
  deleteRecurringDonation(id: string) {
    return axios.delete(`/me/donations/recurring/${encodeURIComponent(id)}`)
  },
  fetchTaxPackPdf(
    period: string,
    options: { includeDonorAdded: boolean; detailLevel: 'basic' | 'detailed' }
  ) {
    return axios.get(`/me/statements/${encodeURIComponent(period)}/pdf`, {
      params: {
        includeDonorAdded: options.includeDonorAdded,
        detail: options.detailLevel,
      },
      responseType: 'blob',
    })
  },
  // NOTE: PAR
  postPar(payload: any) {
    return axios.post('/tokens', payload, {
      baseURL: import.meta.env.VITE_SWIFTAID_API_URL_PAR,
    })
  },
  // NOTE: Cards
  postCard(payload: any) {
    return axios.post('/cards', payload, {
      baseURL: import.meta.env.VITE_SWIFTAID_API_URL_CARD_LINKED,
    })
  },
  deleteCard(payload: any) {
    return axios.delete(`/me/accounts/${payload}`)
  },
  // NOTE: account
  deleteAccount(payload: any) {
    return axios.delete(`/me/accounts/${payload}`)
  },
  fetchBankSession(payload: { providerId: any; redirectUrl: any }) {
    return axios.post(
      `/me/accounts/bankaccount/session/${payload.providerId}`,
      {
        redirectUrl: payload.redirectUrl,
      }
    )
  },
  postAccount(payload: any) {
    return axios.post(`/me/accounts`, payload, {
      validateStatus: (status) =>
        (status >= 200 && status < 300) || status === 202,
    })
  },
  deletePendingEmailVerification(email: string) {
    return axios.delete(
      `/me/accounts/pending-verifications/${encodeURIComponent(email)}`,
      {
        validateStatus: (status) =>
          (status >= 200 && status < 300) || status === 204,
      }
    )
  },
  postAccountVerificationConfirmationCode(payload: any) {
    return axios.post(`/account-verify?confirmation_code=${payload}`)
  },
  fetchDonorPlan() {
    return axios.get('/me/plan', {
      validateStatus: (status) =>
        (status >= 200 && status < 300) || status === 404,
    })
  },
  createPaymentSession() {
    return axios.post('/me/payment-sessions')
  },
  completePaymentSession(sessionId: string) {
    return axios.post(`/me/payment-sessions/${sessionId}/complete`)
  },
  fetchBillingPortalSession() {
    return axios.get('/me/billing-portal')
  },
  // NOTE: Claim tax back. Real API should match CLAIM_TAX_BACK_HANDOVER.md.
  // GET 404 currently hides the feature (mock always 200s an empty profile).
  fetchClaimTaxBackProfile() {
    return axios.get('/me/claim-tax-back/profile', {
      validateStatus: (status) =>
        (status >= 200 && status < 300) || status === 404,
    })
  },
  putClaimTaxBackProfile(payload: Record<string, unknown>) {
    return axios.put('/me/claim-tax-back/profile', payload)
  },
}
